import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { StyledTitle } from 'src/Ghg/components/Table/index.styles';
import { useGhgOrganization } from 'src/common/hooks';
import { handleToastError } from 'src/utils/api-client';
import ConfirmationModal from 'src/components/ConfirmationModal';
import {
  useBulkDeleteEmployeeSurveyResponses,
  useEmployeeSurveyResponses,
} from '../hooks';
import { StyledPanel } from 'src/components/StyledPanel';

const ALL_FILTER_VALUE = '';

export default function EmployeeSurveyPreviewTable() {
  const { t } = useTranslation('ghg', {
    keyPrefix: 'employeeSurveys.previewTable',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const organization = useGhgOrganization();

  const [organizationFilter, setOrganizationFilter] = useState<number | ''>(
    organization.id
  );
  const [periodFilter, setPeriodFilter] = useState<string>(ALL_FILTER_VALUE);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Unfiltered fetch, used only to build the filter dropdown options.
  const { _data: allSurveys } = useEmployeeSurveyResponses();

  const organizationOptions = useMemo(() => {
    const options = new Map<number, string>();
    allSurveys.forEach((survey) =>
      options.set(survey.organization_id, survey.organization_name)
    );
    return Array.from(options.entries()).map(([id, name]) => ({ id, name }));
  }, [allSurveys]);

  const periodOptions = useMemo(() => {
    const relevant =
      organizationFilter === ALL_FILTER_VALUE
        ? allSurveys
        : allSurveys.filter(
            (survey) => survey.organization_id === organizationFilter
          );
    return Array.from(new Set(relevant.map((survey) => survey.period))).sort(
      (a, b) => b.localeCompare(a)
    );
  }, [allSurveys, organizationFilter]);

  const filterParams = useMemo(() => {
    const params: { organization_id?: number; period?: string } = {};
    if (organizationFilter !== ALL_FILTER_VALUE)
      params.organization_id = organizationFilter;
    if (periodFilter !== ALL_FILTER_VALUE) params.period = periodFilter;
    return params;
  }, [organizationFilter, periodFilter]);

  const {
    _data: employeeSurveys,
    isLoading,
    error,
  } = useEmployeeSurveyResponses(filterParams);

  const selectionEnabled = organizationFilter !== ALL_FILTER_VALUE;

  // Prune selection when the filtered rows change (e.g. filters change or a
  // row disappears after a delete) instead of clearing it outright.
  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(employeeSurveys.map((survey) => survey.id));
      const next = new Set<number>();
      let changed = false;
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [employeeSurveys]);

  const handleOrganizationFilterChange = (event: SelectChangeEvent<number>) => {
    const { value } = event.target;
    setOrganizationFilter(value === ALL_FILTER_VALUE ? '' : Number(value));
    setPeriodFilter(ALL_FILTER_VALUE);
    setSelected(new Set());
  };

  const handlePeriodFilterChange = (event: SelectChangeEvent<string>) => {
    setPeriodFilter(event.target.value);
  };

  const allFilteredIds = employeeSurveys.map((survey) => survey.id);
  const allSelected =
    selectionEnabled &&
    allFilteredIds.length > 0 &&
    allFilteredIds.every((id) => selected.has(id));
  const someSelected =
    selectionEnabled && allFilteredIds.some((id) => selected.has(id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allFilteredIds));
    }
  };

  const handleSelectRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkDeleteMutation = useBulkDeleteEmployeeSurveyResponses();

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    if (organizationFilter === ALL_FILTER_VALUE) return;
    bulkDeleteMutation.mutate(
      { organization_id: organizationFilter, ids: Array.from(selected) },
      {
        onSuccess: (data) => {
          toast.success(
            t('bulkDeleteResult.success', { count: data.deleted }) as string
          );
          queryClient.invalidateQueries(['employee-survey-responses']);
          setSelected(new Set());
        },
        onError: (deleteError: any) => {
          if (deleteError?.response?.status === 404) {
            toast.error(t('listChanged') as string);
            queryClient.invalidateQueries(['employee-survey-responses']);
          } else {
            handleToastError(deleteError);
          }
        },
      }
    );
  };

  const handleRowClick = (responseId: number) => {
    navigate(`../../employee-survey-response/${responseId}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('states.loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('states.error', { message: error.message })}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <StyledTitle>{t('title')}</StyledTitle>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="employee-survey-organization-filter-label">
            {t('filters.organization')}
          </InputLabel>
          <Select
            labelId="employee-survey-organization-filter-label"
            label={t('filters.organization')}
            value={organizationFilter === '' ? '' : organizationFilter}
            onChange={handleOrganizationFilterChange}
          >
            <MenuItem value="">{t('filters.all')}</MenuItem>
            {organizationOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="employee-survey-period-filter-label">
            {t('filters.period')}
          </InputLabel>
          <Select
            labelId="employee-survey-period-filter-label"
            label={t('filters.period')}
            value={periodFilter}
            onChange={handlePeriodFilterChange}
          >
            <MenuItem value="">{t('filters.all')}</MenuItem>
            {periodOptions.map((period) => (
              <MenuItem key={period} value={period}>
                {period}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selected.size > 0 && selectionEnabled && organization.can_write_data && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography>
            {t('selectedCount', { count: selected.size })}
          </Typography>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmDeleteOpen(true)}
          >
            {t('deleteSelected')}
          </Button>
        </Box>
      )}

      {employeeSurveys.length === 0 ? (
        <Alert severity="info">{t('states.empty')}</Alert>
      ) : (
        <StyledPanel>
          <Table sx={{ minWidth: 650 }} aria-label="employee surveys table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    disabled={!selectionEnabled}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': t('selectAll') }}
                  />
                </TableCell>
                <TableCell>{t('columns.id')}</TableCell>
                <TableCell>{t('columns.organizationName')}</TableCell>
                <TableCell>{t('columns.period')}</TableCell>
                <TableCell>{t('columns.createdAt')}</TableCell>
                <TableCell align="center">
                  {t('columns.withoutExtrapolation')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeSurveys.map((survey) => (
                <TableRow
                  key={survey.id}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  hover
                  onClick={() => handleRowClick(survey.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(survey.id)}
                      disabled={!selectionEnabled}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleSelectRow(survey.id)}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {survey.id}
                  </TableCell>
                  <TableCell>{survey.organization_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={survey.period}
                      variant="outlined"
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    {survey.created_at
                      ? new Date(survey.created_at).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={t(
                        `states.extrapolation.${
                          survey.without_extrapolation ? 'yes' : 'no'
                        }`
                      )}
                      color={
                        survey.without_extrapolation ? 'warning' : 'primary'
                      }
                      variant="filled"
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledPanel>
      )}

      <ConfirmationModal
        open={confirmDeleteOpen}
        titleKey="ghg:employeeSurveys.previewTable.bulkDeleteConfirmation.title"
        trueButtonTextKey="ghg:employeeSurveys.previewTable.deleteSelected"
        falseButtonTextKey="common.cancel"
        onTrue={handleConfirmDelete}
        onFalse={() => setConfirmDeleteOpen(false)}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <Typography>
          {t('bulkDeleteConfirmation.text', { count: selected.size })}
        </Typography>
      </ConfirmationModal>
    </Box>
  );
}
